import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, Plus, Search, Trash2, Edit2, X, Check, Mail, User,
  ChevronDown, AlertCircle, Crown, Loader2, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import api from '../../lib/api';
import ProducerLayout from '../../components/producer/ProducerLayout';

const PERMISSIONS_LIST = [
  { key: 'manage_events', label: 'Gestionar Eventos', description: 'Crear y editar eventos' },
  { key: 'manage_tickets', label: 'Gestionar Entradas', description: 'Administrar precios y stock' },
  { key: 'manage_validators', label: 'Gestionar Validadores', description: 'Crear y editar validadores' },
  { key: 'manage_rrpp', label: 'Gestionar RRPP', description: 'Administrar códigos y comisiones' },
  { key: 'manage_invitations', label: 'Enviar Invitaciones', description: 'Crear y enviar invitaciones' },
  { key: 'view_reports', label: 'Ver Reportes', description: 'Acceder a estadísticas' },
  { key: 'manage_settlements', label: 'Solicitar Liquidaciones', description: 'Gestionar pagos' },
  { key: 'scan_tickets', label: 'Escanear Entradas', description: 'Validar tickets en puerta' },
];

const ProducerAdminsPage = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMainAdmin, setIsMainAdmin] = useState(false);
  const [mainAdminEmail, setMainAdminEmail] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    permissions: {
      manage_events: false,
      manage_tickets: false,
      manage_validators: false,
      manage_rrpp: false,
      manage_invitations: false,
      view_reports: false,
      manage_settlements: false,
      scan_tickets: false,
    }
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/producers/me/admins');
      setAdmins(response.data.admins || []);
      setIsMainAdmin(response.data.is_main_admin || false);
      setMainAdminEmail(response.data.main_admin_email || '');
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Error al cargar administradores');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'El nombre es requerido';
    if (!formData.email.trim()) errors.email = 'El email es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Email inválido';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (editingAdmin) {
        // Update admin
        await api.put(`/producers/me/admins/${editingAdmin.id}`, {
          name: formData.name,
          permissions: formData.permissions
        });
        toast.success('Admin actualizado');
      } else {
        // Create new admin
        await api.post('/producers/me/admins', formData);
        toast.success('Admin creado. El usuario verá el portal en "Mi Cuenta" al iniciar sesión.');
      }
      
      fetchAdmins();
      closeModal();
    } catch (error) {
      console.error('Error saving admin:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin) => {
    try {
      await api.put(`/producers/me/admins/${admin.id}`, {
        is_active: !admin.is_active
      });
      toast.success(admin.is_active ? 'Admin desactivado' : 'Admin activado');
      fetchAdmins();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDelete = async (admin) => {
    if (!window.confirm(`¿Eliminar a ${admin.name}? Esta acción no se puede deshacer.`)) return;
    
    try {
      await api.delete(`/producers/me/admins/${admin.id}`);
      toast.success('Admin eliminado');
      fetchAdmins();
    } catch (error) {
      toast.error('Error al eliminar admin');
    }
  };

  const openCreateModal = () => {
    setFormData({
      name: '',
      email: '',
      permissions: {
        manage_events: false,
        manage_tickets: false,
        manage_validators: false,
        manage_rrpp: false,
        manage_invitations: false,
        view_reports: false,
        manage_settlements: false,
        scan_tickets: false,
      }
    });
    setFormErrors({});
    setEditingAdmin(null);
    setShowCreateModal(true);
  };

  const openEditModal = (admin) => {
    setFormData({
      name: admin.name,
      email: admin.email,
      permissions: admin.permissions || {}
    });
    setFormErrors({});
    setEditingAdmin(admin);
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingAdmin(null);
  };

  const togglePermission = (key) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key]
      }
    }));
  };

  const filteredAdmins = admins.filter(a =>
    a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <ProducerLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout>
      <div className="space-y-6 p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Shield className="text-violet-500" />
              Administradores
            </h1>
            <p className="text-zinc-400 mt-1">
              Agrega administradores para gestionar tu productora
            </p>
          </div>
          {isMainAdmin && (
            <Button 
              onClick={openCreateModal}
              className="bg-gradient-to-r from-violet-600 to-pink-600 text-white hover:from-violet-500 hover:to-pink-500"
              data-testid="create-admin-btn"
            >
              <Plus size={18} className="mr-2" />
              Nuevo Admin
            </Button>
          )}
        </div>

        {/* Info Banner */}
        {!isMainAdmin && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-amber-400 font-medium">Solo el administrador principal puede gestionar otros administradores</p>
                <p className="text-zinc-400 text-sm mt-1">
                  El administrador principal es: <strong className="text-white">{mainAdminEmail}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Admin Card */}
        <div className="bg-gradient-to-br from-violet-600/20 to-pink-600/20 border border-violet-500/30 rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
              <Crown size={24} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">Administrador Principal</h3>
                <span className="px-2 py-0.5 text-xs bg-violet-500/30 text-violet-300 rounded-full border border-violet-500/50">
                  Todos los permisos
                </span>
              </div>
              <p className="text-zinc-400 text-sm">{mainAdminEmail}</p>
            </div>
          </div>
        </div>

        {/* Search */}
        {admins.length > 0 && (
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="pl-10 bg-zinc-900 border-zinc-800"
              data-testid="admin-search"
            />
          </div>
        )}

        {/* Admins List */}
        {filteredAdmins.length === 0 && admins.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-12 text-center">
            <Shield size={48} className="mx-auto mb-4 text-zinc-600" />
            <h3 className="text-lg font-medium text-white mb-2">
              No hay administradores adicionales
            </h3>
            <p className="text-zinc-500 mb-6">
              Agrega administradores para que otros usuarios puedan gestionar tu productora
            </p>
            {isMainAdmin && (
              <Button onClick={openCreateModal} className="bg-violet-500 text-white hover:bg-violet-600">
                <Plus size={18} className="mr-2" />
                Agregar primer admin
              </Button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredAdmins.map((admin) => (
              <motion.div
                key={admin.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-zinc-900/50 border rounded-xl p-4 ${
                  admin.is_active ? 'border-zinc-800' : 'border-red-900/50 bg-red-950/10'
                }`}
                data-testid={`admin-card-${admin.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Info */}
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      admin.is_active ? 'bg-violet-500/20' : 'bg-zinc-800'
                    }`}>
                      <User size={24} className={admin.is_active ? 'text-violet-500' : 'text-zinc-500'} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white">{admin.name}</h3>
                        {!admin.is_active && (
                          <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full border border-red-500/30">
                            Desactivado
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-zinc-400">
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {admin.email}
                        </span>
                      </div>
                      {/* Permissions Preview */}
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(admin.permissions || {}).filter(([_, v]) => v).slice(0, 4).map(([key]) => (
                          <span 
                            key={key}
                            className="px-2 py-0.5 text-xs bg-zinc-800 text-zinc-300 rounded-full"
                          >
                            {PERMISSIONS_LIST.find(p => p.key === key)?.label || key}
                          </span>
                        ))}
                        {Object.values(admin.permissions || {}).filter(v => v).length > 4 && (
                          <span className="px-2 py-0.5 text-xs bg-zinc-800 text-zinc-400 rounded-full">
                            +{Object.values(admin.permissions || {}).filter(v => v).length - 4} más
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {isMainAdmin && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(admin)}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`edit-admin-${admin.id}`}
                      >
                        <Edit2 size={16} className="mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleStatus(admin)}
                        className={admin.is_active ? 'text-zinc-400 hover:text-red-400' : 'text-zinc-400 hover:text-green-400'}
                        data-testid={`toggle-status-${admin.id}`}
                      >
                        {admin.is_active ? (
                          <>
                            <ToggleRight size={16} className="mr-1" />
                            Desactivar
                          </>
                        ) : (
                          <>
                            <ToggleLeft size={16} className="mr-1" />
                            Activar
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(admin)}
                        className="text-zinc-400 hover:text-red-400"
                        data-testid={`delete-admin-${admin.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* How it works section */}
        <div className="bg-zinc-900/30 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <AlertCircle size={20} className="text-violet-500" />
            ¿Cómo funciona?
          </h3>
          <div className="space-y-3 text-sm text-zinc-400">
            <p>
              <strong className="text-white">1.</strong> Agrega el email de la persona que quieres que administre tu productora.
            </p>
            <p>
              <strong className="text-white">2.</strong> Selecciona los permisos que tendrá (eventos, entradas, validadores, etc.).
            </p>
            <p>
              <strong className="text-white">3.</strong> Cuando esa persona inicie sesión con su cuenta, verá el "Portal Productora" disponible en su sección "Mi Cuenta" → "Mis Portales".
            </p>
            <p className="text-violet-400">
              <strong>Nota:</strong> Solo vos (administrador principal) podés agregar o eliminar otros administradores.
            </p>
          </div>
        </div>

        {/* Create/Edit Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-zinc-800">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-white">
                      {editingAdmin ? 'Editar Admin' : 'Nuevo Admin'}
                    </h2>
                    <button onClick={closeModal} className="text-zinc-500 hover:text-white">
                      <X size={24} />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1">
                      Nombre *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Juan Pérez"
                      className={`bg-zinc-800 border-zinc-700 ${formErrors.name ? 'border-red-500' : ''}`}
                      data-testid="admin-name-input"
                    />
                    {formErrors.name && <p className="text-red-400 text-xs mt-1">{formErrors.name}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-1">
                      Email *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="juan@ejemplo.com"
                      disabled={!!editingAdmin}
                      className={`bg-zinc-800 border-zinc-700 ${formErrors.email ? 'border-red-500' : ''} ${editingAdmin ? 'opacity-50' : ''}`}
                      data-testid="admin-email-input"
                    />
                    {formErrors.email && <p className="text-red-400 text-xs mt-1">{formErrors.email}</p>}
                    {!editingAdmin && (
                      <p className="text-xs text-zinc-500 mt-1">
                        El usuario debe tener una cuenta en la plataforma con este email
                      </p>
                    )}
                  </div>

                  {/* Permissions */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-3">
                      Permisos
                    </label>
                    <div className="space-y-2">
                      {PERMISSIONS_LIST.map(perm => (
                        <label
                          key={perm.key}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            formData.permissions[perm.key]
                              ? 'bg-violet-500/20 border border-violet-500/50'
                              : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.permissions[perm.key] || false}
                            onChange={() => togglePermission(perm.key)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded flex items-center justify-center ${
                            formData.permissions[perm.key]
                              ? 'bg-violet-500 text-white'
                              : 'bg-zinc-700'
                          }`}>
                            {formData.permissions[perm.key] && <Check size={14} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white text-sm">{perm.label}</p>
                            <p className="text-xs text-zinc-500">{perm.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={closeModal}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 bg-violet-500 text-white hover:bg-violet-600"
                      data-testid="save-admin-btn"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin mr-2" />
                          Guardando...
                        </>
                      ) : (
                        editingAdmin ? 'Guardar cambios' : 'Crear admin'
                      )}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </ProducerLayout>
  );
};

export default ProducerAdminsPage;
